/**
 * testmode.js — deterministic mode plus the `window.__cc` automation contract.
 *
 * Activated by `?test=1`. When active:
 *   - all gameplay randomness is seeded (mulberry32) and therefore repeatable
 *   - networking is disabled and stub modules record their calls
 *   - the tutorial is skipped by default
 *   - `window.__cc` is installed for Playwright to drive the game
 *
 * Every module that needs a random number must call `rng()` from this module
 * rather than Math.random, or determinism is lost and stall tests go flaky.
 *
 * The `__cc` surface is a CONTRACT — other agents' tests depend on it.
 * Its shape is documented in ARCHITECTURE.md; change it only additively.
 *
 * @module core/testmode
 */

import { GAME_VERSION, STALL_IDS, computeTokens } from '../constants.js';
import { bus } from './events.js';

// ─────────────────────────────────────────────────────────────────────────────
// URL parameters
// ─────────────────────────────────────────────────────────────────────────────

const search = typeof window !== 'undefined' ? window.location.search : '';
const params = new URLSearchParams(search);

function flag(name, fallback = false) {
  if (!params.has(name)) return fallback;
  const v = params.get(name);
  return v === '' || v === '1' || v === 'true';
}

/** Runtime switches derived from the query string. */
export const options = Object.freeze({
  /** `?test=1` — deterministic mode + `window.__cc`. */
  test: flag('test'),
  /** `?fresh=1` — ignore any stored save. */
  fresh: flag('fresh'),
  /** `?stall=<id>` — auto-approach and activate a stall once ready. */
  stall: params.get('stall'),
  /** `?skiptutorial=1` — defaults ON in test mode. */
  skipTutorial: flag('skiptutorial', flag('test')),
  /** `?seed=<int>` — override the deterministic seed. */
  seed: Number(params.get('seed') || 0) || 0x5eed1234,
  /** `?vr=0` disables the XR button (used to force desktop paths). */
  vr: !params.has('vr') || flag('vr', true),
  /** `?perf=1` — show the performance overlay. */
  perf: flag('perf'),
  /** `?nophysics=1` — escape hatch for isolating render issues. */
  noPhysics: flag('nophysics'),
});

// ─────────────────────────────────────────────────────────────────────────────
// Seeded RNG
// ─────────────────────────────────────────────────────────────────────────────

/**
 * mulberry32 — small, fast, good enough for gameplay variation and, crucially,
 * reproducible from a seed.
 * @param {number} seed
 * @returns {() => number} Generator producing [0, 1).
 */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let _rng = options.test ? mulberry32(options.seed) : Math.random;

/**
 * The game's only source of randomness.
 * @returns {number} [0, 1)
 */
export function rng() {
  return _rng();
}

/** @param {number} a @param {number} b @returns {number} Uniform in [a, b). */
export function randRange(a, b) {
  return a + (b - a) * _rng();
}

/** @param {number} a @param {number} b @returns {number} Integer in [a, b]. */
export function randInt(a, b) {
  return Math.floor(a + (b - a + 1) * _rng());
}

/** @template T @param {T[]} arr @returns {T} A random element. */
export function pick(arr) {
  return arr[Math.floor(_rng() * arr.length)];
}

/** Re-seed the generator. Test mode calls this on reset for repeatability. */
export function reseed(seed = options.seed) {
  _rng = options.test ? mulberry32(seed) : Math.random;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stub call log — every stub module records here so tests can assert on
// "did the game try to open the prize desk?" without a real implementation.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Record a call made into a stub module.
 * @param {string} module e.g. 'meta'
 * @param {string} method e.g. 'prizeDesk.open'
 * @param {any[]} args
 */
export function logCall(module, method, args = []) {
  if (typeof window === 'undefined') return;
  if (!window.__ccCalls) window.__ccCalls = [];
  window.__ccCalls.push({ module, method, args: safeArgs(args), t: Date.now() });
  if (window.__ccCalls.length > 500) window.__ccCalls.shift();
}

/** Strip anything that will not survive structured cloning to the test runner. */
function safeArgs(args) {
  return args.map((a) => {
    if (a === null || a === undefined) return a;
    const t = typeof a;
    if (t === 'number' || t === 'string' || t === 'boolean') return a;
    if (Array.isArray(a)) return a.slice(0, 8).map((x) => (typeof x === 'object' ? '[obj]' : x));
    if (t === 'object') {
      const out = {};
      for (const k of Object.keys(a).slice(0, 12)) {
        const v = a[k];
        out[k] = typeof v === 'object' && v !== null ? '[obj]' : v;
      }
      return out;
    }
    return String(a);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// The __cc automation API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Subsystem references, injected by main.js once they exist. Keeping them
 * behind this indirection is what stops testmode.js from importing (and
 * therefore coupling to) every module in the game.
 * @type {{ engine?: any, physics?: any, avatar?: any, stalls?: any,
 *          teleport?: any, hud?: any, economy?: any, save?: any }}
 */
const wired = {};

let _resolveReady;
const readyPromise = new Promise((resolve) => { _resolveReady = resolve; });
let _ready = false;

/**
 * Give test mode access to a live subsystem.
 * @param {Record<string, any>} refs
 */
export function provide(refs) {
  Object.assign(wired, refs);
}

/** Resolve `window.__cc.ready`. Called after the first frame + physics init. */
export function markReady() {
  if (_ready) return;
  _ready = true;
  _resolveReady();
}

/** @returns {boolean} */
export function isReady() {
  return _ready;
}

/**
 * Install `window.__cc` and the global error capture.
 * Safe to call in non-test mode — the API is useful for manual debugging, and
 * only the seeded RNG and stub logging are gated behind `?test=1`.
 */
export function install() {
  if (typeof window === 'undefined') return;

  window.__ccErrors = [];
  window.__ccCalls = [];

  window.addEventListener('error', (e) => {
    window.__ccErrors.push(String(e.message || e.error || 'unknown error'));
  });
  window.addEventListener('unhandledrejection', (e) => {
    window.__ccErrors.push(`unhandled rejection: ${e.reason && e.reason.message ? e.reason.message : e.reason}`);
  });

  if (options.test) bus.startLogging();

  const api = {
    ready: readyPromise,
    version: GAME_VERSION,
    options: { ...options },

    /** Coarse snapshot of everything a test usually wants at once. */
    state() {
      return {
        version: GAME_VERSION,
        testMode: options.test,
        mode: wired.avatar ? wired.avatar.getMode() : 'unknown',
        activeStall: wired.stalls ? (wired.stalls.activeId() || null) : null,
        physicsReady: wired.physics ? wired.physics.isReady() : false,
        xrSupported: wired.xr ? wired.xr.isSupported() : false,
        inXR: wired.xr ? wired.xr.isPresenting() : false,
        frameCount: wired.engine ? wired.engine.frameCount : 0,
        fps: wired.engine ? Math.round(wired.engine.fps) : 0,
        bodies: wired.physics ? wired.physics.bodyCount() : 0,
        wallet: wired.economy ? wired.economy.wallet() : null,
      };
    },

    economy: {
      tickets: () => wired.economy.tickets(),
      tokens: () => wired.economy.tokens(),
      stars: () => wired.economy.stars(),
      wallet: () => wired.economy.wallet(),
      grant: (amounts) => wired.economy.grant(amounts, 'test'),
      /** Spend tickets. @returns {boolean} */
      spend: (n) => wired.economy.spendTickets(n, 'test'),
      spendTokens: (n) => wired.economy.spendTokens(n, 'test'),
    },

    stalls: {
      /** @returns {Array<{id,name,cost,tier,implemented}>} */
      list: () => wired.stalls.list(),
      /** @returns {Promise<boolean>} true if the round started. */
      activate: (id) => wired.stalls.activate(id),
      /** @returns {object|null} Live state of a stall. */
      state: (id) => wired.stalls.stateOf(id),
      /**
       * Fire a scripted throw without any VR hardware.
       * @param {{hand?:'left'|'right', velocity:[number,number,number],
       *          objectIndex?:number, origin?:[number,number,number]}} opts
       * @returns {object|null} Info about the throw, or null if unavailable.
       */
      simulateThrow: (opts) => wired.stalls.simulateThrow(opts),
      /** Force the current round to end immediately. @returns {Promise<object|null>} */
      completeRound: () => wired.stalls.completeRound(),
      /** Pay again and restart the same stall. @returns {Promise<boolean>} */
      playAgain: () => wired.stalls.playAgain(),
      /** Leave the active stall and return to the hub. */
      exit: () => wired.stalls.exit(),

      /**
       * Scoring targets of the active stall.
       * @returns {Array<{id, group, position, localPosition, scored}>}
       */
      targets: () => wired.stalls.targets(),
      /**
       * Reposition a target in STALL-LOCAL space and freeze it — for staging a
       * scoring situation physics alone cannot be relied on to produce.
       * @param {string} targetId
       * @param {number[]} localPos
       * @param {number[]} [rotation] World quaternion [x,y,z,w].
       * @returns {boolean}
       */
      moveTarget: (targetId, localPos, rotation) =>
        wired.stalls.moveTarget(targetId, localPos, rotation),
      /**
       * Where aim assist would pull a throw released from `localOrigin`.
       * @returns {{ origin: number[], target: number[]|null }|null}
       */
      aimTarget: (localOrigin) => wired.stalls.aimTarget(localOrigin),
      /** The GDD 7.3 payout curve, so a test need not import constants.js. */
      computeTokens: (stallId, score) => computeTokens(stallId, score),
      /** Registered ids that actually have an implementation. */
      implemented: () => wired.stalls.list().filter((s) => s.implemented).map((s) => s.id),
      /** All 12 launch ids from the GDD, implemented or not. */
      all: () => [...STALL_IDS],
      /**
       * Stall-specific diagnostic state, when the active stall implements
       * `debugState()`. The extension point for internals no generic API
       * models — per-ball bounce flags, rim contacts, which bowl caught what.
       * Null for a stall that does not provide one.
       * @returns {object|null}
       */
      debug: () => {
        const active = wired.stalls ? wired.stalls.active : null;
        return active && typeof active.debugState === 'function' ? active.debugState() : null;
      },

      /**
       * Invoke a stall-specific test hook on the ACTIVE stall.
       *
       * Some stalls cannot be driven through `simulateThrow` at all — nothing
       * is thrown at High Striker or Whack-a-Mole — and staging their scoring
       * situations through physics alone would be flaky. Those stalls implement
       * `testHook(name, args)` and route it to the same internal method real
       * gameplay calls, so a test still exercises the production scoring path.
       *
       * Returns null when no stall is active, or the stall does not know `name`.
       *
       * @param {string} name
       * @param {object} [args]
       * @returns {any}
       */
      action(name, args) {
        const active = wired.stalls ? wired.stalls.active : null;
        if (!active || typeof active.testHook !== 'function') return null;
        return active.testHook(name, args || {});
      },

      /**
       * High Striker: strike the pad with an exact mallet-head velocity. Pass
       * `velocityY` for a pure downward swing, or `velocity: [x, y, z]` to prove
       * that sideways motion scores nothing (GDD 6.6.1).
       * @param {{ velocityY?: number, velocity?: number[] }} [opts]
       * @returns {{ downward: number, zone: string|null, points: number }|null}
       */
      simulateStrike: (opts) => api.stalls.action('strike', opts || {}),

      /**
       * Whack-a-Mole: raise the mole at a hole index, with an optional type
       * ('standard' | 'golden' | 'bomb') and visibility window in seconds.
       * @param {{ index?: number, type?: string, seconds?: number }} [opts]
       */
      pokeMole: (opts) => api.stalls.action('pop', opts || {}),

      /**
       * Whack-a-Mole: land a mallet-head hit on a hole index. Scores nothing if
       * that mole is not currently up — the GDD 6.6.2 "miss" case.
       * @param {{ index?: number, side?: 'left'|'right' }} [opts]
       */
      whackMole: (opts) => api.stalls.action('whack', opts || {}),

      /**
       * Advance the ACTIVE stall's round clock without waiting in real time.
       *
       * Hook a Duck runs 60 s and Coin Pusher 101 s; a test that sat through
       * either on a software renderer would be measured in minutes. This drives
       * the same per-frame update the engine drives, in fixed steps, so timers,
       * phase changes and kinematic motion all advance exactly as they would
       * live. It does NOT step physics — falling bodies stay where they are, so
       * stage those with `dropCoins` or a scripted hook rather than expecting
       * the pile to move.
       *
       * @param {number} sec
       * @returns {number} Seconds actually advanced (short if the round ended).
       */
      fastForward: (sec = 1) => {
        const active = wired.stalls ? wired.stalls.active : null;
        if (!active) return 0;
        const step = 1 / 60;
        let t = 0;
        let guard = 0;
        while (t + 1e-6 < sec && guard++ < 20_000) {
          active._update(Math.min(step, sec - t));
          t += Math.min(step, sec - t);
          if (!active.roundActive) break;
        }
        return t;
      },

      /**
       * Hook a Duck: place the hook on a duck's ring and lift (GDD 6.7.1).
       * `duckId` is a target id from `targets()`, whose `group` is the duck
       * size — 'large', 'small' or 'golden'.
       * @param {string} duckId
       * @returns {boolean} True if the duck was caught.
       */
      hookAt: (duckId) => api.stalls.action('hookAt', { duckId }),

      /**
       * Coin Pusher: inject coins deterministically (GDD 6.7.2).
       * `zone` is 'slot' (the player's path, through the chute), 'edge'
       * (already past the front lip, counted by the next scan) or 'gutter'
       * (out at the side, lost and never counted).
       * @param {number} n
       * @param {number} [xOffset] Lateral offset, metres.
       * @param {{ zone?: 'slot'|'edge'|'gutter', token?: boolean }} [opts]
       * @returns {number|null} How many were spawned.
       */
      dropCoins: (n, xOffset, opts) =>
        api.stalls.action('dropCoins', { n, xOffset, ...(opts || {}) }),

      /**
       * Duck Gallery: pull the trigger at a chosen aim point (GDD 6.5.1).
       * Runs the real muzzle raycast and the real scoring code — only the aim is
       * scripted rather than mouse-driven, and the ~3/s fire cap still applies,
       * so consecutive shots need a `fastForward` between them.
       *
       * @param {string|number[]|{id: string, spot?: 'body'|'bullseye'}} target
       *   A target id from `targets()`, a world point, or `{ id, spot }` to pick
       *   a duck's body plate (50) or its bullseye (200).
       * @returns {{hit: boolean, targetId: string|null, points: number,
       *            label: string}|null} null if the shot fell inside the cap.
       */
      fireAt: (target) => api.stalls.action('fire', { target }),

      /**
       * Duck Gallery: surface the golden duck now for its full two-second
       * window, instead of waiting on its RNG schedule. Pass `{ only: true }`
       * to drop the rest of the schedule, so a test asserting on the window
       * cannot have a scheduled appearance land inside it.
       * @param {{ only?: boolean }} [opts]
       * @returns {string|null} Its target id.
       */
      raiseGolden: (opts) => api.stalls.action('golden', opts || {}),
    },

    avatar: {
      /** @returns {'third'|'first'} */
      mode: () => wired.avatar.getMode(),
      /** @returns {[number,number,number]} */
      position: () => wired.avatar.getPosition().toArray(),
      /** @returns {boolean} */
      teleportTo: (nodeId) => wired.teleport.to(nodeId),
      /** @returns {string[]} */
      nodes: () => wired.teleport.listNodes(),
    },

    time: {
      /** Shift the save's day-based clocks. @returns {number} New day index. */
      advanceDays: (n) => wired.save.advanceDays(n),
      /** @returns {number} Current (possibly offset) day index. */
      today: () => wired.save.today(),
    },

    save: {
      reset: () => { wired.save.reset(); reseed(); return true; },
      snapshot: () => wired.save.snapshot(),
      export: () => wired.save.export(),
      import: (json) => wired.save.import(json),
      flush: () => { wired.save.flush(); return true; },
    },

    render: {
      frameCount: () => (wired.engine ? wired.engine.frameCount : 0),
      fps: () => (wired.engine ? Math.round(wired.engine.fps) : 0),
      /**
       * Resolve after `n` rendered frames. Physics advances with the render
       * loop, so this is also how a test waits for objects to settle.
       * @param {number} n
       * @returns {Promise<number>} The frame count on resolve.
       */
      waitFrames: (n = 1) => wired.engine.waitFrames(n),
      /** Resolve after roughly `sec` seconds of simulated frames. */
      waitSeconds: (sec = 1) => wired.engine.waitFrames(Math.ceil(sec * 60)),
    },

    events: {
      /** @returns {Array<{name,payload,t}>} Recent bus emissions. */
      log: () => bus.getLog(),
      /** Count emissions of one event name. */
      count: (name) => bus.getLog().filter((e) => e.name === name).length,
      /** Wait for the next emission of an event. */
      next: (name, timeoutMs = 10_000) => bus.next(name, timeoutMs),
    },

    /** The seeded generator, so tests can assert determinism without importing. */
    rng: () => rng(),
    reseed: (seed) => { reseed(seed); return true; },

    /**
     * Networking. Real connections are always off under `?test=1`, but the
     * pure parts — the protocol, the replay arc maths, the visit flow — are
     * fully exercisable offline, which is what everything below the first
     * three entries is for. `simulateInbound` feeds a message through the same
     * validate-and-render path a real peer's would take.
     */
    net: {
      isEnabled: () => (wired.net ? wired.net.isEnabled() : false),
      isConnected: () => (wired.net ? wired.net.presence.isConnected() : false),
      connect: (room) => (wired.net ? wired.net.presence.connect(room) : Promise.resolve(false)),

      /** Pack→unpack a message of any of the 9 types. @returns {object} */
      protocolTest: (spec) => wired.net.protocolTest(spec),
      /** Closed-form ballistic position at time t. @returns {number[]} */
      arcAt: (origin, velocity, gravityScale, t) =>
        wired.net.arcAt(origin, velocity, gravityScale, t),
      /** Where a neighbour's lane sits at a stall. @returns {object|null} */
      laneAnchor: (stallId, laneIndex) => wired.net.laneAnchor(stallId, laneIndex),
      /** Render a cosmetic replay arc without a peer. @returns {boolean} */
      spawnReplay: (data) => wired.net.spawnReplay(data),
      /** Live replay state, including the always-zero body count. */
      replayState: () => wired.net.replayState(),
      /** Push a raw wire message in as if a peer had sent it. */
      simulateInbound: (peerId, message) => wired.net.simulateInbound(peerId, message),
      presenceState: () => wired.net.presenceState(),
      /** What the bottom-right presence badge is showing. */
      badge: () => wired.net.badge(),
      /** Serialize→render→react round trip against the space module. */
      visitTest: (opts) => wired.net.visitTest(opts),
      visitState: () => wired.net.visitState(),
      peers: () => wired.net.peers(),
      room: () => wired.net.room(),
      disconnect: () => wired.net.disconnect(),
    },

    /**
     * Ambient NPC geese. They run in test mode too — deterministically, from a
     * plan drawn once at install — because the hub having life in it is a
     * property worth asserting, not just a thing to look at.
     */
    npc: {
      /** @returns {Array<{id,name,position,facing,state,target}>} */
      list: () => (wired.net ? wired.net.npcGeese.list() : []),
      /** Full snapshot: waypoints, per-goose plan, visited sequence. */
      state: () => (wired.net ? wired.net.npcGeese.state() : null),
      /** Make one honk on demand. @returns {boolean} */
      honk: (i) => (wired.net ? wired.net.npcGeese.honk(i) : false),
    },

    /**
     * Meta economy — prize sets, cosmetics, daily login, weekly challenges,
     * leaderboards and the demo store. Supplied by `meta/index.js` through
     * `provide({ meta })`, so testmode.js never imports the meta layer.
     */
    get meta() { return wired.meta || null; },

    /**
     * Personal space — room, shelves, fish tank, decor, guestbook and the
     * visitor view (GDD 9). The module owns the shape; see
     * `space/test-api.js`. Supplied through `provide({ space })`, so
     * testmode.js never imports the space layer.
     */
    get space() { return wired.space ? wired.space.testApi : null; },

    /**
     * First-run onboarding (GDD §4). Test mode skips the tutorial by default,
     * so a test that wants to drive it boots with `?test=1&skiptutorial=0` and
     * calls `start()` — or simply asserts `state().active === false` to prove
     * the default is intact.
     */
    tutorial: {
      /** @returns {object|null} active, index, stepId, title, marker, … */
      state: () => (wired.tutorial ? wired.tutorial.state() : null),
      /** Begin onboarding. @returns {boolean} False if done or already live. */
      start: () => (wired.tutorial ? wired.tutorial.start() : false),
      /** Abandon it and mark it complete. @returns {boolean} */
      skip: () => (wired.tutorial ? wired.tutorial.skip() : false),
      /**
       * Complete the current step as if the player had performed it — for
       * walking the flow without staging six pieces of real gameplay.
       * @returns {boolean}
       */
      advance: () => (wired.tutorial ? wired.tutorial.advance() : false),
      /** @returns {string[]} Step ids in order. */
      steps: () => (wired.tutorial ? wired.tutorial.steps() : []),
    },

    /** `?perf=1` overlay numbers: fps, draw calls, triangles, body count. */
    perf: () => (wired.perf ? wired.perf.sample() : null),

    /**
     * Celebration bursts. `count()` is the number alive right now, which is
     * what makes the "they are disposed rather than accumulated" property
     * assertable — a burst that never dies is a leak no screenshot would show.
     */
    vfx: {
      count: () => (wired.vfx ? wired.vfx.count() : 0),
      confetti: () => { wired.vfx?.confetti(); return true; },
      sparkle: () => { wired.vfx?.sparkle(); return true; },
      clear: () => { wired.vfx?.clear(); return true; },
    },

    /** Calls recorded by the stub modules (meta/space/net/audio). */
    calls: () => (window.__ccCalls || []).slice(),
    errors: () => (window.__ccErrors || []).slice(),
  };

  window.__cc = api;
  return api;
}
